import React, { useEffect, useState } from "react";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate,
  useLocation,
} from "react-router-dom";
import "@/utils/errorLogger"; // Initialize error logging for development
import { Loader2 } from "lucide-react";
import PayrollLogin from "./components/PayrollLogin";
import Dashboard from "./components/Dashboard";
import EmployeeManagement from "./components/EmployeeManagement";
import EmployerManagement from "./components/EmployerManagement";
import Signup from "./components/Signup";
import EmployeeOverview from "@/components/EmployeeOverview";
import RunPayroll from "@/components/PayrunRun";
import { Toaster } from "@/components/ui/toaster";
import TimeOff from "@/components/TimeOff";
import NannyTimeOff from "@/components/NannyTimeOff";
import PolicyConfig from "@/components/pto/PolicyConfig";
import Earnings from "@/components/Earnings";
import PayrollApproval from "@/components/PayrunApproval";
import LandingPage from "@/components/LandingPage";
import PensionsOverview from "@/components/pensions/PensionsOverview";
import FilingsGrid from "@/components/FilingsGrid";
import ParentInformation from "@/components/ParentInformation";
import NannySubmission from "@/components/submission/NannySubmission";
import NannyRegister from "@/components/NannyRegister";
import EmployeeSubmissions from "@/components/submission/EmployeeSubmissions";
import NannySubmissionsList from "@/components/submission/NannySubmissionsList";
import EmployerSubmissionView from "@/components/submission/EmployerSubmissionView";
import { useAuth } from "@/hooks/useAuth";
import EmployeePayslips from "@/components/employee/EmployeePayslips";
import {
  isNannyUser,
  isPartnerUser,
  isContractorUser,
  User,
  isClientUser,
  hasAccessToSystem,
} from "@/types/user";
import AdminEditor from "@/components/superuser/AdminEditor";
import EPSManagement from "@/components/superuser/EPSManagement";
import BillingFlow from "@/components/billing/BillingFlow";
import AffiliateDashboard from "@/components/billing/AffiliateDashboard";
import EmploymentAgreementForm from "@/components/employment/EmploymentAgreementForm";
import EmployerContractsView from "@/components/employment/EmployerContractsView";
import NannyAgreementView from "@/components/employment/NannyAgreementView";
import NannyPensionActions from "@/components/pensions/NannyPensionActions";
import SubscriptionSuccess from "@/components/billing/SubscriptionComplete";
import { SetupProvider } from "@/contexts/SetupContext";
import { CountryProvider } from "@/contexts/CountryContext";
import { FeatureFlagProvider } from "@/contexts/FeatureFlagContext";
import PartnerRegistration from "@/components/partner/PartnerRegistration";
import ImplementationStatusManager from "@/components/pensions/ImplementationStatusManager";
import OperationsDashboard from "@/components/operations/OperationsDashboard";
import EmployerDetailView from "@/components/operations/EmployerDetail";
import IssueDetail from "@/components/operations/IssueDetail";
import TerminationContainer from "@/components/termination/TerminationContainer";
import ResetPassword from "@/components/auth/ResetPassword";
import RequestDemoPage from "@/pages/RequestDemoPage";
import RequestContractorDemoPage from "@/pages/RequestContractorDemoPage";
import { TourProvider } from "@/contexts/TourContext";
import { EmployeeProvider } from "@/contexts/EmployeeContext";
import { TourWrapper } from "@/components/tour/TourWrapper";
import ThemeDemo from "@/components/ThemeDemo";
import FontDemo from "./components/FontDemo";
import LiteEmploymentAgreementForm from "@/components/employment/LiteEmploymentAgreementForm";
import ContractorQuiz from "./components/contractor/Quiz";
import ContractorAgreementForm from "@/components/contractor/ContractorAgreementForm";
import SalaryCalculator from "./components/salary-calculator/SalaryCalculator";
import FeatureList from "./components/operations/FeatureList";
import FeatureFlagUserManagement from "./components/operations/FeatureFlagUserManagement";
import ContractorDashboard from "@/components/contractor/ContractorDashboard";
import OperationsContractorDashboard from "@/components/operations/OperationsContractorDashboard";
import ContractorEntityWizard from "@/components/contractor/ContractorEntityWizard";
import { InvoicesContainer } from "@/components/contractor/InvoicesContainer";
import ContractorContractsContainer from "@/components/contractor/ContractorContractsContainer";
import { StripeOAuthCallback } from "@/components/contractor/StripeOAuthCallback";
import { ContractorSettings } from "@/components/contractor/ContractorSettings";
import ClientRegister from "@/components/ClientRegister";
import ClientContractsContainer from "@/components/client/ClientContractsContainer";
import { ClientInvoicesContainer } from "@/components/client/ClientInvoicesContainer";
import ExpenseManagementPage from "@/pages/ExpenseManagementPage";
import SubscriptionRequired from "@/components/SubscriptionRequired";
import Logout from "@/components/auth/Logout";
import { Profile } from "@/components/Profile";
import { USOnboardingWizard } from "@/components/onboarding";
import { SmartBotWidget } from "@/components/customer-ai/SmartBotWidget";
import MultiAgentSmartBotWidget from "@/components/customer-ai/MultiAgentSmartBotWidget";
import { ContractBuilderPage } from "@/pages/ContractBuilderPage";
import { ContractEditorAccessPage } from "@/pages/ContractEditorAccessPage";
import { DashboardContractBuilder } from "@/components/contract-builder/DashboardContractBuilder";
import { PayrollValidatorUploadPage } from "@/pages/PayrollValidatorUploadPage";
import { PayrollValidatorResultsPage } from "@/pages/PayrollValidatorResultsPage";
import PayrollValidatorDemoPage from "@/pages/PayrollValidatorDemoPage";

interface ProtectedRouteProps {
  children: React.ReactNode | ((props: { user: User }) => React.ReactElement);
  requiresEmployer?: boolean;
  requiresContractor?: boolean;
  requiresNanny?: boolean;
  requiresSuperuser?: boolean;
  requiresClient?: boolean;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requiresEmployer = false,
  requiresContractor = false,
  requiresNanny = false,
  requiresSuperuser = false,
  requiresClient = false,
}) => {
  const location = useLocation();

  const { user, isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated || !user) {
    return <Navigate to="/login" replace />;
  }

  // Check if user has access to the system (subscription requirement)
  // Allow access to billing flow and logout even without subscription
  const allowedPathsWithoutSubscription = [
    "/billing",
    "/subscription/complete",
    "/logout",
  ];

  const shouldCheckSubscription = !allowedPathsWithoutSubscription.some(
    (path) => location.pathname.startsWith(path),
  );

  if (shouldCheckSubscription && !hasAccessToSystem(user)) {
    return <SubscriptionRequired />;
  }

  if (isPartnerUser(user) && !location.pathname.startsWith("/referrals")) {
    return <Navigate to="/referrals" replace />;
  }

  if (requiresSuperuser && user.userType === "superuser") {
    return typeof children === "function" ? (
      children({ user })
    ) : (
      <>{children}</>
    );
  }

  if (requiresEmployer && !isNannyUser(user)) {
    return typeof children === "function" ? (
      children({ user })
    ) : (
      <>{children}</>
    );
  }

  if (requiresNanny && isNannyUser(user)) {
    return typeof children === "function" ? (
      children({ user })
    ) : (
      <>{children}</>
    );
  }

  if (requiresContractor && isContractorUser(user)) {
    return typeof children === "function" ? (
      children({ user })
    ) : (
      <>{children}</>
    );
  }

  if (requiresClient && isClientUser(user)) {
    return typeof children === "function" ? (
      children({ user })
    ) : (
      <>{children}</>
    );
  }

  if (!requiresEmployer && !requiresNanny && !requiresSuperuser) {
    return typeof children === "function" ? (
      children({ user })
    ) : (
      <>{children}</>
    );
  }

  return <Navigate to="/dashboard" replace />;
};

const TimeOffRoute: React.FC = () => {
  const { user } = useAuth();
  return user && isNannyUser(user) ? <NannyTimeOff /> : <TimeOff />;
};

const App: React.FC = () => {
  const { initialize, user } = useAuth();

  useEffect(() => {
    initialize();
  }, []);

  return (
    <AuthInitializer>
      <Router>
        <FeatureFlagProvider>
          <CountryProvider contractorId={user?.id?.toString()}>
            <SetupProvider>
              <TourProvider>
                <EmployeeProvider>
                <Routes>
                  <Route
                    path="/partner/register"
                    element={<PartnerRegistration />}
                  />
                  <Route path="/demo/request" element={<RequestDemoPage />} />
                  <Route
                    path="/contractor-demo-request"
                    element={<RequestContractorDemoPage />}
                  />
                  <Route path="/billing" element={<BillingFlow />} />
                  <Route
                    path="/subscription/complete"
                    element={<SubscriptionSuccess />}
                  />
                  <Route path="/login" element={<PayrollLogin />} />
                  <Route
                    path="/reset-password/:token"
                    element={<ResetPassword />}
                  />
                  <Route path="/landingPage" element={<LandingPage />} />
                  <Route path="/theme-demo" element={<ThemeDemo />} />
                  <Route path="/font-demo" element={<FontDemo />} />
                  <Route
                    path="/nanny/register/:inviteCode"
                    element={<NannyRegister />}
                  />
                  <Route
                    path="/client/register/:inviteCode"
                    element={<ClientRegister />}
                  />
                  <Route path="/contractor-quiz" element={<ContractorQuiz />} />
                  <Route
                    path="/salary-calculator"
                    element={<SalaryCalculator />}
                  />
                  <Route
                    path="/payroll-validator"
                    element={<PayrollValidatorUploadPage />}
                  />
                  <Route
                    path="/payroll-validator/session/:sessionId"
                    element={<PayrollValidatorResultsPage />}
                  />
                  <Route
                    path="/payroll-validator-demo"
                    element={<PayrollValidatorDemoPage />}
                  />
                  <Route
                    path="/employment-agreement"
                    element={<LiteEmploymentAgreementForm />}
                  />
                  <Route
                    path="/contractor-agreement"
                    element={<ContractorAgreementForm />}
                  />
                  <Route
                    path="/contract-builder"
                    element={<ContractBuilderPage />}
                  />
                  <Route
                    path="/contract-editor/:accessToken"
                    element={<ContractEditorAccessPage />}
                  />
                  <Route
                    path="/admin/code-editor"
                    element={
                      <ProtectedRoute requiresSuperuser>
                        <AdminEditor />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/subscriptions/implementation"
                    element={
                      <ProtectedRoute requiresSuperuser>
                        <ImplementationStatusManager />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/superuser/eps-management"
                    element={
                      <ProtectedRoute requiresSuperuser>
                        <EPSManagement />
                      </ProtectedRoute>
                    }
                  />
                  {/* Common Protected Routes */}
                  <Route
                    path="/contract/generate"
                    element={
                      <ProtectedRoute>
                        <EmploymentAgreementForm />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/contract/edit/:agreementId"
                    element={
                      <ProtectedRoute requiresEmployer>
                        <EmploymentAgreementForm />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/lite/contract/generate"
                    element={<LiteEmploymentAgreementForm />}
                  />
                  <Route
                    path="/contractor/agreement/generate"
                    element={
                      <ProtectedRoute requiresContractor>
                        <ContractorAgreementForm />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/dashboard"
                    element={
                      <ProtectedRoute>
                        <Dashboard />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/operations/dashboard"
                    element={
                      <ProtectedRoute requiresSuperuser>
                        <OperationsDashboard />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/operations/employer/:employerId"
                    element={
                      <ProtectedRoute requiresSuperuser>
                        <EmployerDetailView />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/operations/contractor/:contractorId"
                    element={
                      <ProtectedRoute requiresSuperuser>
                        <OperationsContractorDashboard />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/operations/issue/:issueId"
                    element={
                      <ProtectedRoute requiresSuperuser>
                        <IssueDetail />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/employees"
                    element={
                      <ProtectedRoute requiresEmployer>
                        <EmployeeManagement />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/pensions"
                    element={
                      <ProtectedRoute requiresEmployer>
                        <PensionsOverview />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/employer"
                    element={
                      <ProtectedRoute requiresEmployer>
                        <EmployerManagement />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/parent-info"
                    element={
                      <ProtectedRoute requiresEmployer>
                        <ParentInformation />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/contracts"
                    element={
                      <ProtectedRoute requiresEmployer>
                        <EmployerContractsView />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/dashboard/contract-builder"
                    element={
                      <ProtectedRoute requiresEmployer>
                        <DashboardContractBuilder />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/employee/:employeeId"
                    element={
                      <ProtectedRoute>
                        <EmployeeOverview />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/time-off"
                    element={
                      <ProtectedRoute requiresEmployer>
                        <TimeOff />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/earnings"
                    element={
                      <ProtectedRoute requiresEmployer>
                        <Earnings />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/run-payroll/:payrunId"
                    element={
                      <ProtectedRoute requiresEmployer>
                        <RunPayroll />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/payroll-approval/:payrunId"
                    element={
                      <ProtectedRoute requiresEmployer>
                        <PayrollApproval />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/pto-policy"
                    element={
                      <ProtectedRoute requiresEmployer>
                        <PolicyConfig />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/time-off/policy/:id"
                    element={
                      <ProtectedRoute requiresEmployer>
                        <PolicyConfig />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/filings"
                    element={
                      <ProtectedRoute>
                        <FilingsGrid />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/terminate/:employeeId"
                    element={
                      <ProtectedRoute requiresEmployer>
                        <TerminationContainer />
                      </ProtectedRoute>
                    }
                  />
                  {/* Nanny Routes */}
                  <Route
                    path="/nanny/time-off"
                    element={
                      <ProtectedRoute requiresNanny>
                        <NannyTimeOff />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/nanny/submission/:submissionId?"
                    element={
                      <ProtectedRoute requiresNanny>
                        <NannySubmission />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/nanny/submissions"
                    element={
                      <ProtectedRoute requiresNanny>
                        <NannySubmissionsList />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/nanny/pensions"
                    element={
                      <ProtectedRoute requiresNanny>
                        <NannyPensionActions />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/nanny/payslips"
                    element={
                      <ProtectedRoute requiresNanny>
                        <EmployeePayslips />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/nanny/contract"
                    element={
                      <ProtectedRoute requiresNanny>
                        <NannyAgreementView />
                      </ProtectedRoute>
                    }
                  />
                  {/* Contractor Routes */}
                  <Route
                    path="/contractor/dashboard"
                    element={
                      <ProtectedRoute requiresContractor>
                        <ContractorDashboard />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/contractor/profile"
                    element={
                      <ProtectedRoute requiresContractor>
                        <ContractorEntityWizard />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/contractor/settings"
                    element={
                      <ProtectedRoute requiresContractor>
                        <ContractorSettings />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/contractor/onboarding/us"
                    element={
                      <ProtectedRoute requiresContractor>
                        {({ user }) => (
                          <USOnboardingWizard
                            contractorId={user.id?.toString()}
                          />
                        )}
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/contractor/invoices"
                    element={
                      <ProtectedRoute requiresContractor>
                        <InvoicesContainer />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/contractor/contracts"
                    element={
                      <ProtectedRoute requiresContractor>
                        <ContractorContractsContainer />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/contractor/expenses"
                    element={
                      <ProtectedRoute requiresContractor>
                        <ExpenseManagementPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/contractor/stripe/callback"
                    element={
                      <ProtectedRoute requiresContractor>
                        <StripeOAuthCallback />
                      </ProtectedRoute>
                    }
                  />

                  {/* Client Routes */}
                  <Route
                    path="/client/contracts"
                    element={
                      <ProtectedRoute requiresClient>
                        <ClientContractsContainer />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/client/invoices"
                    element={
                      <ProtectedRoute requiresClient>
                        <ClientInvoicesContainer />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/client/expenses"
                    element={
                      <ProtectedRoute requiresClient>
                        <ExpenseManagementPage />
                      </ProtectedRoute>
                    }
                  />

                  {/* Superuser Routes */}
                  <Route
                    path="/ops/features"
                    element={
                      <ProtectedRoute requiresSuperuser>
                        <FeatureList />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/operations/features/:flagKey/users"
                    element={
                      <ProtectedRoute requiresSuperuser>
                        <FeatureFlagUserManagement />
                      </ProtectedRoute>
                    }
                  />

                  {/* Employee submissions routes */}
                  <Route
                    path="/employee-submissions"
                    element={
                      <ProtectedRoute requiresEmployer>
                        <EmployeeSubmissions />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/submissions/:employeeId"
                    element={
                      <ProtectedRoute requiresEmployer>
                        <EmployeeSubmissions />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/submission/:submissionId"
                    element={
                      <ProtectedRoute requiresEmployer>
                        <EmployerSubmissionView />
                      </ProtectedRoute>
                    }
                  />

                  {/* Partner Routes */}
                  <Route
                    path="/referrals"
                    element={
                      <ProtectedRoute>
                        <AffiliateDashboard />
                      </ProtectedRoute>
                    }
                  />

                  {/* Profile Route */}
                  <Route
                    path="/profile"
                    element={
                      <ProtectedRoute>
                        <Profile />
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/"
                    element={
                      <ProtectedRoute>
                        <Dashboard />
                      </ProtectedRoute>
                    }
                  />
                  <Route path="/logout" element={<Logout />} />
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
                <Toaster />
                <TourWrapper />

                {/* AI Assistant - Available on all pages */}
                <MultiAgentSmartBotWidget
                  hideOnPages={["/", "/login", "/signup", "/reset-password", "/contract-builder", "/dashboard/contract-builder"]}
                />
                </EmployeeProvider>
              </TourProvider>
            </SetupProvider>
          </CountryProvider>
        </FeatureFlagProvider>
      </Router>
    </AuthInitializer>
  );
};

const AuthInitializer: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { isLoading, initialized } = useAuth();

  if (!initialized || isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return <>{children}</>;
};

export default App;
